import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, Menu, X, Send, Megaphone } from "lucide-react";
import { YCN_LOGO_URL } from "@/data/constants";
import WatchNowButton from "@/components/watch/WatchNowButton";
import { cn } from "@/lib/utils";

const navLinks = [
  { to: "/", label: "Home", end: true, testId: "nav-home" },
  { to: "/movies", label: "Movies", testId: "nav-movies" },
  { to: "/shows", label: "TV Shows", testId: "nav-shows" },
  { to: "/about", label: "About", testId: "nav-about" },
  { to: "/awards", label: "Awards", testId: "nav-awards" },
  { to: "/submit", label: "Submission", testId: "nav-submit", icon: Send, accent: true },
  { to: "/advertise", label: "Advertise With Us", testId: "nav-advertise", icon: Megaphone, accent: true },
];

export default function Header() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b",
        scrolled
          ? "bg-[#050505]/92 backdrop-blur-xl border-white/10"
          : "bg-gradient-to-b from-black/85 via-black/40 to-transparent border-transparent"
      )}
    >
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 h-20 md:h-24 flex items-center gap-4 md:gap-8">
        <Link to="/" data-testid="header-logo-link" aria-label="Your Channel Network — home" className="flex items-center gap-3 shrink-0 group">
          <img src={YCN_LOGO_URL} alt="YCN — Your Channel Network" className="h-12 md:h-16 w-auto drop-shadow-[0_0_18px_rgba(124,58,237,0.35)] group-hover:drop-shadow-[0_0_24px_rgba(249,115,22,0.45)] transition" />
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-7 ml-2">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.testId}
              className={({ isActive }) =>
                cn(
                  "text-sm font-medium transition-colors relative py-1 flex items-center gap-1.5",
                  l.accent
                    ? "px-3 py-1.5 rounded-full border border-white/15 hover:border-white/40 bg-white/[0.03] hover:bg-white/[0.08]"
                    : "",
                  isActive ? "text-white" : "text-white/70 hover:text-white"
                )
              }
            >
              {({ isActive }) => (
                <>
                  {l.icon && <l.icon size={13} className={l.accent ? "text-orange-300" : ""} />}
                  {l.label}
                  {isActive && !l.accent && (
                    <span className="absolute -bottom-1 left-0 right-0 h-0.5 rounded-full bg-gradient-to-r from-violet-500 via-blue-500 to-orange-500" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3 ml-auto">
          <button
            data-testid="header-search-btn"
            aria-label="Search"
            className="hidden md:inline-flex h-10 w-10 items-center justify-center rounded-full text-white/80 hover:text-white hover:bg-white/10 transition"
            onClick={() => navigate("/browse")}
          >
            <Search size={18} />
          </button>

          <WatchNowButton
            testId="header-watch-cta"
            className="hidden md:inline-flex"
          />

          <button
            data-testid="header-mobile-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Menu"
            className="lg:hidden h-10 w-10 flex items-center justify-center rounded-full text-white hover:bg-white/10"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-[#050505]/95 backdrop-blur-xl border-t border-white/10">
          <div className="px-6 py-4 space-y-1">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={`m-${l.testId}`}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  cn("block py-3 text-base border-b border-white/5",
                    isActive ? "text-white font-semibold" : "text-white/70")
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/browse" onClick={() => setMobileOpen(false)} data-testid="m-browse-cta" className="mt-3 block py-3 text-center rounded-full font-semibold ycn-glass hover:bg-white/15 transition">Browse Catalog</Link>
            <div className="mt-2" onClick={() => setMobileOpen(false)}>
              <WatchNowButton testId="m-watch-cta" className="w-full justify-center" />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
